/**
 * Пропорциональная SVG-карта помещений для 3D-вида.
 *
 * Единый масштаб общего контура сохраняет реальные соотношения размеров комнат.
 * Холст — по пропорциям плана, а не квадрат: у вытянутой квартиры нет пустых полей.
 * Единица холста — пиксель экрана, поэтому подписи одного размера на любом плане.
 */
import { useProjectStore } from "@/shared/store";
import { roomDisplayName, roomLabelCeilingHeight, roomLabelText, type Room } from "@/widgets/plan";

interface Props {
	rooms: Room[];
	ceilingHeights: Record<string, number | undefined>;
	selectedRoomId: string | null;
	onSelectRoom: (roomId: string) => void;
}

/** Ширина карты, пикселей. */
const MAP_WIDTH = 176;
/** Высота карты — по пропорции плана, но в этих пределах, пикселей. */
const MAP_MIN_HEIGHT = 90;
const MAP_MAX_HEIGHT = 190;
const PADDING = 4;
/** Кегль подписей, пикселей. */
const LABEL_FONT = 9;

export const RoomMapCanvas = ({ rooms, ceilingHeights, selectedRoomId, onSelectRoom }: Props) => {
	const roomNames = useProjectStore((s) => s.project.layers.rooms);
	const points = rooms.flatMap((room) => room.inner);
	const minX = Math.min(...points.map((point) => point.x));
	const maxX = Math.max(...points.map((point) => point.x));
	const minY = Math.min(...points.map((point) => point.y));
	const maxY = Math.max(...points.map((point) => point.y));
	const width = Math.max(1, maxX - minX);
	const height = Math.max(1, maxY - minY);
	const mapHeight = Math.min(
		MAP_MAX_HEIGHT,
		Math.max(MAP_MIN_HEIGHT, (MAP_WIDTH - PADDING * 2) * (height / width) + PADDING * 2),
	);
	const scale = Math.min((MAP_WIDTH - PADDING * 2) / width, (mapHeight - PADDING * 2) / height);
	const offsetX = (MAP_WIDTH - width * scale) / 2 - minX * scale;
	const offsetY = (mapHeight - height * scale) / 2 - minY * scale;
	const toPoint = (x: number, y: number) => `${offsetX + x * scale},${offsetY + y * scale}`;

	return (
		<svg
			viewBox={`0 0 ${MAP_WIDTH} ${mapHeight}`}
			className="block w-full rounded-sm"
			aria-label="Карта помещений"
		>
			{rooms.map((room, index) => {
				const selected = room.id === selectedRoomId;
				const name = roomDisplayName(roomNames, room.id, index);
				const area = roomLabelText(room.area);
				const ceilingHeight = ceilingHeights[room.id];
				const ceilingMark =
					ceilingHeight === undefined ? undefined : roomLabelCeilingHeight(ceilingHeight);
				const labelX = offsetX + room.label.x * scale;
				const labelY = offsetY + room.label.y * scale;
				// В узкой комнате — только название, в совсем крошечной — ничего: подпись
				// не должна вылезать за контур (полное — во всплывающей подсказке)
				const xs = room.inner.map((point) => point.x);
				const ys = room.inner.map((point) => point.y);
				const roomWidth = (Math.max(...xs) - Math.min(...xs)) * scale;
				const roomHeight = (Math.max(...ys) - Math.min(...ys)) * scale;
				const fits = (text: string, lines: number) =>
					text.length * LABEL_FONT * 0.56 + 4 <= roomWidth &&
					lines * LABEL_FONT * 1.25 + 2 <= roomHeight;
				const lines = [name, area, ceilingMark].filter(
					(line): line is string => line !== undefined,
				);
				const longest = lines.reduce((a, b) => (a.length >= b.length ? a : b));
				const shown = fits(longest, lines.length) ? lines : fits(name, 1) ? [name] : [];
				return (
					<g key={room.id}>
						<polygon
							points={room.inner.map((point) => toPoint(point.x, point.y)).join(" ")}
							fill={selected ? "var(--color-accent-soft)" : "var(--color-surface)"}
							stroke={selected ? "var(--color-accent)" : "var(--color-border)"}
							strokeWidth={selected ? 2.4 : 1.2}
							className="cursor-pointer transition-colors hover:fill-accent-soft"
							onClick={() => onSelectRoom(room.id)}
						>
							<title>{[name, area, ceilingMark].filter(Boolean).join(" · ")}</title>
						</polygon>
						{shown.length > 0 && (
							<text
								x={labelX}
								y={labelY}
								textAnchor="middle"
								dominantBaseline="middle"
								fontSize={LABEL_FONT}
								fill={selected ? "var(--color-accent)" : "var(--color-ink)"}
								className="pointer-events-none select-none"
							>
								{shown.map((line, i) => (
									<tspan
										key={line}
										x={labelX}
										dy={i === 0 ? `${-((shown.length - 1) * 1.25) / 2}em` : "1.25em"}
									>
										{line}
									</tspan>
								))}
							</text>
						)}
					</g>
				);
			})}
		</svg>
	);
};
